import {
	DeviceAuthentication,
	DeviceBatteryLevel,
	DeviceCompatibilityLevel,
	DeviceHealth,
	DeviceLicense,
	DeviceLicenseWarningDetails,
	DeviceMicState,
	DeviceModel,
	DeviceStatus
} from '@shure/shared/angular/data-access/system-api/models';
import { FirmwarePackage } from '@shure/shared/models';

import { Auracast } from './auracast';
import { CloudGateway } from './cloud-gateway';
import { ExtendedWarranty } from './extended-warranty';
import { FirmwareUpdateRequest, isFirmwareUpdateRequestActive } from './firmware-update-requests';
import { LicenseV3 } from './license-v3';
import { LicenseV4 } from './license-v4';
import { MissingFeatureType } from './missing-feature-type';
import { NetworkInterface } from './network-interface';
import { PropertyPanelDevice } from './property-panel-device.model';
import { ProxiedDevice } from './proxied-device';
import { DeviceScheduledJobFeature } from './scheduled-jobs';
import { SortedDeviceConditionCollection } from './sorted-device-condition-collection';

export interface InventoryDevice {
	id: string;
	name: string;
	model: DeviceModel;
	virtual: boolean;
	status: DeviceStatus;
	hardwareId?: string;
	compatibility: DeviceCompatibilityLevel;
	manufacturer?: string;
	features: {
		audioChannelCount?: {
			count: number;
		} & MissingFeatureType;
		audioNetwork?: NetworkInterface & MissingFeatureType;
		auracast?: Auracast & MissingFeatureType;
		authentication?: DeviceAuthentication & MissingFeatureType;
		availablePackages?: {
			primaryPackages: FirmwarePackage[];
			hostedPackages: FirmwarePackage[];
		} & MissingFeatureType;
		batteryLevel?: DeviceBatteryLevel & MissingFeatureType;
		cloudGateway?: CloudGateway & MissingFeatureType;
		controlNetwork?: NetworkInterface & MissingFeatureType;
		danteName?: {
			danteName: string;
		} & MissingFeatureType;
		extendedWarranty?: ExtendedWarranty & MissingFeatureType;
		health?: DeviceHealth & MissingFeatureType;
		identify?: {
			identifying: boolean;
		} & MissingFeatureType;
		iotRoomProxy?: {
			enabled: boolean;
		} & MissingFeatureType;
		license?: DeviceLicense<DeviceLicenseWarningDetails> & MissingFeatureType;
		licenseV3?: {
			license?: LicenseV3;
		} & MissingFeatureType;
		licenseV4?: {
			license?: LicenseV4;
		} & MissingFeatureType;
		micStatus?: {
			status: DeviceMicState;
		} & MissingFeatureType;
		mute?: {
			muted: boolean;
		} & MissingFeatureType;
		proxiedDevices?: {
			proxiedDevices: ProxiedDevice[];
		} & MissingFeatureType;
		scheduledJobs?: DeviceScheduledJobFeature & MissingFeatureType;
		serialNumber?: {
			serialNumber: string;
		} & MissingFeatureType;
		room?: {
			room: {
				name: string;
			};
		} & MissingFeatureType;
		tags?: {
			tags: string[];
		} & MissingFeatureType;

		// though not alphabetical, all Firmware and FirmwareUpdate related features are grouped
		firmware?: {
			version: string;
			valid: boolean;
			isUpToDate: boolean;
		} & MissingFeatureType;
		hostedFirmware?: FirmwarePackage & MissingFeatureType;
		primaryFirmwareUpdateRequest?: { currentRequest: FirmwareUpdateRequest } & MissingFeatureType;
		hostedFirmwareUpdateRequest?: { currentRequest: FirmwareUpdateRequest } & MissingFeatureType;
	};
	conditions: SortedDeviceConditionCollection;
}

export function isDeviceUpdating(device: InventoryDevice | PropertyPanelDevice): boolean {
	return (
		isFirmwareUpdateRequestActive(device.features.primaryFirmwareUpdateRequest?.currentRequest.status) ||
		isFirmwareUpdateRequestActive(device.features.hostedFirmwareUpdateRequest?.currentRequest.status)
	);
}
